// store/financialSlice.ts
// The financial-profile slice. Types, defaults, migrations, and thunks live in
// sibling modules and are re-exported here so every existing
// `from '.../store/financialSlice'` import keeps resolving.

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	CASH_RUNWAY_ALLOCATION_ID,
	type Assumptions,
	type EquityGrant,
	type EquityType,
	type ExpenseEntry,
	type ExpenseGroup,
	type FinancialState,
	type IncomeCategory,
	type IncomeEntry,
	type LifestyleAsset,
	type LifestyleAssetKind,
	type PortfolioMode,
	type ReviewedAssumptions,
	type StockHolding,
	type SurplusAllocationEntry,
	type SurplusAllocationGroup,
} from './financialTypes';
import {
	DEFAULT_ASSUMPTIONS,
	MAX_OVERRIDE_YEARS,
	uid,
} from './financialDefaults';
import {
	buildInitialState,
	migrateExpenseEntry,
	migrateHolding,
	migrateIncomeEntry,
	migrateIncomeGrowthRate,
	migrateReturnRate,
	resolveInflationRate,
} from './financialMigration';
import {
	fetchFinancialProfile,
	saveFinancialProfile,
} from './financialThunks';

const financialSlice = createSlice({
	name: 'financial',
	initialState: buildInitialState(),
	reducers: {
		setAge(state, action: PayloadAction<string>) {
			state.age = action.payload;
			state._lastUpdated = Date.now();
		},
		setSavings(state, action: PayloadAction<string>) {
			state.savings = action.payload;
			state._lastUpdated = Date.now();
		},
		setInvestedSavings(state, action: PayloadAction<string>) {
			state.investedSavings = action.payload;
			state._lastUpdated = Date.now();
		},
		// Anonymous quick start. One atomic write mapping the four numbers a
		// visitor gives us onto the REAL model: scalars plus one salary row and
		// one "Monthly spending" row, so a later signup lands in a Dashboard that
		// already makes sense.
		applyQuickStart(
			state,
			action: PayloadAction<{
				age: string;
				monthlyIncome: string;
				monthlySpending: string;
				savings: string;
				invested: string;
			}>,
		) {
			const p = action.payload;
			state.age = p.age;
			state.savings = p.savings;
			state.investedSavings = p.invested;
			state.incomeEntries = [
				{
					id: uid(),
					name: 'Take-home income',
					amount: p.monthlyIncome,
					frequency: 'monthly',
				},
			];
			state.expenseEntries = [
				{
					id: uid(),
					name: 'Monthly spending',
					amount: p.monthlySpending,
					frequency: 'monthly',
					group: 'flexible_lifestyle',
					flexibility: 'medium',
					includeInMonthlySpending: true,
				},
			];
			state._lastUpdated = Date.now();
		},
		// ── Income ──
		addIncomeEntry(
			state,
			action: PayloadAction<{ category?: IncomeCategory } | undefined>,
		) {
			state.incomeEntries.push({
				id: uid(),
				name: '',
				amount: '',
				frequency: 'monthly',
				category: action?.payload?.category,
			});
			state._lastUpdated = Date.now();
		},
		updateIncomeEntry(
			state,
			action: PayloadAction<{ id: string; patch: Partial<IncomeEntry> }>,
		) {
			const entry = state.incomeEntries.find(
				(e) => e.id === action.payload.id,
			);
			if (entry) Object.assign(entry, action.payload.patch);
			state._lastUpdated = Date.now();
		},
		removeIncomeEntry(state, action: PayloadAction<string>) {
			state.incomeEntries = state.incomeEntries.filter(
				(e) => e.id !== action.payload,
			);
			state._lastUpdated = Date.now();
		},
		// ── Expenses ──
		addExpenseEntry(
			state,
			action: PayloadAction<{ group?: ExpenseGroup } | undefined>,
		) {
			state.expenseEntries.push({
				id: uid(),
				name: '',
				amount: '',
				frequency: 'monthly',
				// New rows from a group section pre-fill that group; a bare add
				// lands in 'other' for the user to categorize.
				group: action.payload?.group ?? 'other',
				flexibility: undefined,
				includeInMonthlySpending: true,
			});
			state._lastUpdated = Date.now();
		},
		updateExpenseEntry(
			state,
			action: PayloadAction<{ id: string; patch: Partial<ExpenseEntry> }>,
		) {
			const entry = state.expenseEntries.find(
				(e) => e.id === action.payload.id,
			);
			if (entry) Object.assign(entry, action.payload.patch);
			state._lastUpdated = Date.now();
		},
		removeExpenseEntry(state, action: PayloadAction<string>) {
			state.expenseEntries = state.expenseEntries.filter(
				(e) => e.id !== action.payload,
			);
			state._lastUpdated = Date.now();
		},
		// ── Lifestyle assets ──
		addLifestyleAsset(
			state,
			action: PayloadAction<{
				label: string;
				value: string;
				kind: LifestyleAssetKind;
			}>,
		) {
			state.lifestyleAssets.push({
				id: uid(),
				label: action.payload.label,
				value: action.payload.value,
				kind: action.payload.kind,
			});
			state._lastUpdated = Date.now();
		},
		updateLifestyleAsset(
			state,
			action: PayloadAction<{ id: string; patch: Partial<LifestyleAsset> }>,
		) {
			const asset = state.lifestyleAssets.find(
				(a) => a.id === action.payload.id,
			);
			if (asset) Object.assign(asset, action.payload.patch);
			state._lastUpdated = Date.now();
		},
		removeLifestyleAsset(state, action: PayloadAction<string>) {
			state.lifestyleAssets = state.lifestyleAssets.filter(
				(a) => a.id !== action.payload,
			);
			state._lastUpdated = Date.now();
		},
		// ── Surplus allocations ──
		// Money aimed at the future. These describe where surplus goes; they do
		// NOT reduce surplus (surplus = income − spending). Keeping them out of
		// the expense total prevents "investing more makes you worse off".
		addSurplusAllocation(
			state,
			action: PayloadAction<
				{ group?: SurplusAllocationGroup } | undefined
			>,
		) {
			state.surplusAllocations.push({
				id: uid(),
				name: '',
				amount: '',
				frequency: 'monthly',
				group: action.payload?.group ?? 'investing',
			});
			state._lastUpdated = Date.now();
		},
		updateSurplusAllocation(
			state,
			action: PayloadAction<{
				id: string;
				patch: Partial<SurplusAllocationEntry>;
			}>,
		) {
			const entry = state.surplusAllocations.find(
				(e) => e.id === action.payload.id,
			);
			if (entry) Object.assign(entry, action.payload.patch);
			state._lastUpdated = Date.now();
		},
		removeSurplusAllocation(state, action: PayloadAction<string>) {
			state.surplusAllocations = state.surplusAllocations.filter(
				(e) => e.id !== action.payload,
			);
			state._lastUpdated = Date.now();
		},
		// Cash runway. Money Flow's "Cash runway" card writes here: a single
		// cash_savings allocation with a STABLE id so it never duplicates.
		// Empty/zero removes it.
		upsertCashRunway(state, action: PayloadAction<{ amount: string }>) {
			const { amount } = action.payload;
			const amt = parseFloat(amount);
			const cleared = isNaN(amt) || amt <= 0;
			const existing = state.surplusAllocations.find(
				(a) => a.id === CASH_RUNWAY_ALLOCATION_ID,
			);
			if (cleared) {
				if (existing) {
					state.surplusAllocations = state.surplusAllocations.filter(
						(a) => a.id !== CASH_RUNWAY_ALLOCATION_ID,
					);
					state._lastUpdated = Date.now();
				}
				return;
			}
			if (existing) {
				existing.amount = amount;
				existing.frequency = 'monthly';
				existing.group = 'cash_savings';
			} else {
				state.surplusAllocations.push({
					id: CASH_RUNWAY_ALLOCATION_ID,
					name: 'Cash runway',
					amount,
					frequency: 'monthly',
					group: 'cash_savings',
				});
			}
			state._lastUpdated = Date.now();
		},
		setCurrentRunwaySaved(state, action: PayloadAction<string>) {
			state.currentRunwaySaved = action.payload;
			state._lastUpdated = Date.now();
		},
		// ── Holdings ──
		addHolding(
			state,
			action: PayloadAction<{ symbol: string; companyName: string }>,
		) {
			state.holdings.unshift({
				id: uid(),
				symbol: action.payload.symbol,
				companyName: action.payload.companyName,
				shares: '',
				avgPrice: '',
				monthlyContribution: '',
				growthRate: '',
				growthRateSource: 'none' as const,
				currentPrice: '',
				priceUpdatedAt: null,
				assetClass: 'single-stock' as const,
				priceHistory: [],
				// V13 — a fresh holding's avgPrice will be typed by the user.
				avgPriceSource: 'user' as const,
			});
			state._lastUpdated = Date.now();
		},
		updateHolding(
			state,
			action: PayloadAction<{ id: string; patch: Partial<StockHolding> }>,
		) {
			const h = state.holdings.find((h) => h.id === action.payload.id);
			if (h) {
				Object.assign(h, action.payload.patch);
				// V13 — every user edit of avgPrice CONFIRMS it as cost basis
				// (clearing any seeded/unknown pre-fix provenance), unless the
				// patch stated a provenance itself.
				if (
					action.payload.patch.avgPrice !== undefined &&
					action.payload.patch.avgPriceSource === undefined
				) {
					h.avgPriceSource = 'user';
				}
				// V13 — a deliberate manual-value edit carries its own date; a
				// cleared value drops the date with it.
				if (
					action.payload.patch.manualCurrentValue !== undefined &&
					action.payload.patch.manualValueUpdatedAt === undefined
				) {
					h.manualValueUpdatedAt = action.payload.patch
						.manualCurrentValue.trim()
						? Date.now()
						: null;
				}
				// A holding awaiting its position (post-ticker-change or freshly
				// activated) is complete the moment both shares and average price
				// are present, so clear the flag without a second explicit action.
				if (
					h.awaitingPosition &&
					parseFloat(h.shares) > 0 &&
					parseFloat(h.avgPrice) > 0
				) {
					h.awaitingPosition = false;
				}
			}
			state._lastUpdated = Date.now();
		},
		// V13 rework — one-click confirmation of a pre-fix average price:
		// "Yes, this is what I paid" stamps the provenance without changing
		// the number. Editing avgPrice in updateHolding confirms it too.
		confirmCostBasis(state, action: PayloadAction<string>) {
			const h = state.holdings.find((x) => x.id === action.payload);
			if (!h) return;
			h.avgPriceSource = 'user';
			state._lastUpdated = Date.now();
		},
		// Mark a REVIEW-level warning as accepted ("Keep this assumption"),
		// snapshotting the current signal so the insight layer suppresses it until
		// the data meaningfully changes. Blocking kinds are never dismissed here.
		reviewHoldingAssumption(
			state,
			action: PayloadAction<{
				id: string;
				kind: 'manual-rate' | 'flagged-window' | 'stale-price';
			}>,
		) {
			const h = state.holdings.find((x) => x.id === action.payload.id);
			if (!h) return;
			const r: ReviewedAssumptions = h.reviewedAssumptions ?? {};
			switch (action.payload.kind) {
				case 'manual-rate':
					r.manualRate = { rate: h.growthRate };
					break;
				case 'flagged-window':
					r.flaggedWindow = {
						rate: h.growthRate,
						source: h.growthRateSource,
					};
					break;
				case 'stale-price':
					r.stalePrice = { priceUpdatedAt: h.priceUpdatedAt };
					break;
			}
			h.reviewedAssumptions = r;
			state._lastUpdated = Date.now();
		},
		// Undo a review (the quiet "Reviewed ✓ · undo" affordance). Clearing one
		// kind, or all when kind is omitted, re-activates the matching warning.
		clearHoldingReview(
			state,
			action: PayloadAction<{
				id: string;
				kind?: 'manual-rate' | 'flagged-window' | 'stale-price';
			}>,
		) {
			const h = state.holdings.find((x) => x.id === action.payload.id);
			if (!h || !h.reviewedAssumptions) return;
			const { kind } = action.payload;
			if (!kind) {
				h.reviewedAssumptions = undefined;
			} else {
				if (kind === 'manual-rate')
					delete h.reviewedAssumptions.manualRate;
				if (kind === 'flagged-window')
					delete h.reviewedAssumptions.flaggedWindow;
				if (kind === 'stale-price')
					delete h.reviewedAssumptions.stalePrice;
			}
			state._lastUpdated = Date.now();
		},
		removeHolding(state, action: PayloadAction<string>) {
			state.holdings = state.holdings.filter(
				(h) => h.id !== action.payload,
			);
			state._lastUpdated = Date.now();
		},
		// Start a tracked holding with no ticker yet, optionally carrying a monthly
		// contribution handed off from Money Flow. Drafts are excluded from
		// value/projection until activated.
		addDraftHolding(
			state,
			action: PayloadAction<{ monthlyContribution?: string } | undefined>,
		) {
			state.holdings.unshift({
				id: uid(),
				symbol: '',
				companyName: '',
				shares: '',
				avgPrice: '',
				monthlyContribution: action.payload?.monthlyContribution ?? '',
				growthRate: '',
				growthRateSource: 'none' as const,
				currentPrice: '',
				priceUpdatedAt: null,
				assetClass: 'single-stock' as const,
				priceHistory: [],
				avgPriceSource: 'user' as const,
				status: 'draft',
				awaitingPosition: false,
			});
			state._lastUpdated = Date.now();
		},
		// Promote a draft to an active position once a ticker/name is chosen. The
		// row's auto-fetch then fills price/growth; shares/avgPrice stay empty for
		// the user to fill.
		activateHolding(
			state,
			action: PayloadAction<{
				id: string;
				symbol: string;
				companyName: string;
			}>,
		) {
			const h = state.holdings.find((x) => x.id === action.payload.id);
			if (!h) return;
			h.symbol = action.payload.symbol;
			h.companyName = action.payload.companyName;
			h.status = 'active';
			// Fresh identity → let the row's auto-fetch populate price/growth.
			h.growthRate = '';
			h.growthRateSource = 'none';
			h.currentPrice = '';
			h.priceUpdatedAt = null;
			h.priceHistory = [];
			// No shares/avg yet, so the position still needs filling.
			h.awaitingPosition = true;
			state._lastUpdated = Date.now();
		},
		// Change the ticker of an ACTIVE holding. Identity change: keep the monthly
		// contribution, clear shares + avgPrice (stale for a different security)
		// and reset price/growth so the row re-fetches.
		changeHoldingTicker(
			state,
			action: PayloadAction<{
				id: string;
				symbol: string;
				companyName: string;
			}>,
		) {
			const h = state.holdings.find((x) => x.id === action.payload.id);
			if (!h) return;
			h.symbol = action.payload.symbol;
			h.companyName = action.payload.companyName;
			// Identity changed — old position data no longer applies.
			h.shares = '';
			h.avgPrice = '';
			// V13 — the manual value and cost-basis provenance belonged to the
			// old security; a fresh identity starts clean and confirmed-by-user.
			h.manualCurrentValue = undefined;
			h.manualValueUpdatedAt = null;
			h.avgPriceSource = 'user';
			h.growthRate = '';
			h.growthRateSource = 'none';
			h.currentPrice = '';
			h.priceUpdatedAt = null;
			h.priceHistory = [];
			// Needs shares + avgPrice re-entered; auto-cleared by updateHolding
			// once both are filled.
			h.awaitingPosition = true;
			// Accepted reviews no longer apply to this (now different) security.
			h.reviewedAssumptions = undefined;
			// monthlyContribution intentionally preserved.
			state._lastUpdated = Date.now();
		},
		// Discard draft holdings never meaningfully filled (no ticker AND no
		// contribution). Called on navigate-away; a draft with a carried
		// contribution is preserved so the user's work survives.
		discardEmptyDraftHoldings(state) {
			const before = state.holdings.length;
			state.holdings = state.holdings.filter(
				(h) =>
					h.status !== 'draft' ||
					h.symbol !== '' ||
					parseFloat(h.monthlyContribution) > 0,
			);
			if (state.holdings.length !== before)
				state._lastUpdated = Date.now();
		},
		// ── Equity grants ──
		addEquityGrant(
			state,
			action: PayloadAction<{ equityType?: EquityType }>,
		) {
			const equityType = action.payload.equityType ?? 'rsu';
			// Private/illiquid equity defaults to excluded from FI; everything
			// else defaults to "vested only" — paper value never auto-counts.
			const isPrivate =
				equityType === 'private-shares' || equityType === 'other';
			state.equityGrants.push({
				id: uid(),
				label: '',
				equityType,
				currentEstimatedValue: '',
				vestedValue: '',
				unvestedValue: '',
				monthlyVestingValue: '',
				expectedAnnualGrowthRate: '',
				liquidityStatus: isPrivate ? 'private-illiquid' : 'unknown',
				includeInFIProjection: !isPrivate,
				includeOnlyVestedValue: true,
			});
			state._lastUpdated = Date.now();
		},
		updateEquityGrant(
			state,
			action: PayloadAction<{ id: string; patch: Partial<EquityGrant> }>,
		) {
			const g = state.equityGrants.find((g) => g.id === action.payload.id);
			if (g) Object.assign(g, action.payload.patch);
			state._lastUpdated = Date.now();
		},
		removeEquityGrant(state, action: PayloadAction<string>) {
			state.equityGrants = state.equityGrants.filter(
				(g) => g.id !== action.payload,
			);
			state._lastUpdated = Date.now();
		},
		// ── Assumptions ──
		updateAssumptions(state, action: PayloadAction<Partial<Assumptions>>) {
			Object.assign(state.assumptions, action.payload);
			state._lastUpdated = Date.now();
		},
		setPortfolioMode(state, action: PayloadAction<PortfolioMode>) {
			state.assumptions.portfolioMode = action.payload;
			state._lastUpdated = Date.now();
		},
		// ── Year-by-year return overrides ──
		setReturnOverride(
			state,
			action: PayloadAction<{
				yearOffset: number;
				expectedReturnPercent: string;
				note?: string;
			}>,
		) {
			const { yearOffset } = action.payload;
			if (yearOffset < 0 || yearOffset >= MAX_OVERRIDE_YEARS) return;
			const existing = state.assumptions.returnOverrides.find(
				(o) => o.yearOffset === yearOffset,
			);
			if (existing) {
				existing.expectedReturnPercent =
					action.payload.expectedReturnPercent;
				existing.note = action.payload.note;
			} else {
				state.assumptions.returnOverrides.push({
					yearOffset,
					expectedReturnPercent: action.payload.expectedReturnPercent,
					note: action.payload.note,
				});
			}
			state.assumptions.returnOverrides.sort(
				(a, b) => a.yearOffset - b.yearOffset,
			);
			state._lastUpdated = Date.now();
		},
		removeReturnOverride(state, action: PayloadAction<number>) {
			state.assumptions.returnOverrides =
				state.assumptions.returnOverrides.filter(
					(o) => o.yearOffset !== action.payload,
				);
			state._lastUpdated = Date.now();
		},
		clearReturnOverrides(state) {
			state.assumptions.returnOverrides = [];
			state._lastUpdated = Date.now();
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchFinancialProfile.pending, (state) => {
				state._syncStatus = 'pending';
			})
			.addCase(fetchFinancialProfile.fulfilled, (state, action) => {
				// Back-fill new fields so older payloads merge cleanly.
				const p = action.payload as Partial<FinancialState>;
				const effInflationStr = resolveInflationRate(p);
				Object.assign(state, p, {
					incomeEntries: (
						p.incomeEntries ??
						state.incomeEntries ??
						[]
					).map((e) =>
						migrateIncomeEntry(
							e as Partial<IncomeEntry> & { id: string },
						),
					),
					expenseEntries: (
						p.expenseEntries ??
						state.expenseEntries ??
						[]
					).map((e) =>
						migrateExpenseEntry(
							e as Partial<ExpenseEntry> & { id: string },
						),
					),
					surplusAllocations:
						p.surplusAllocations ?? state.surplusAllocations ?? [],
					holdings: (p.holdings ?? state.holdings ?? []).map((h) =>
						migrateHolding(
							h as Partial<StockHolding> & { id: string },
						),
					),
					investedSavings:
						p.investedSavings ?? state.investedSavings ?? '0',
					currentRunwaySaved:
						p.currentRunwaySaved ?? state.currentRunwaySaved ?? '',
					equityGrants: p.equityGrants ?? state.equityGrants ?? [],
					assumptions: {
						...DEFAULT_ASSUMPTIONS,
						...state.assumptions,
						...(p.assumptions ?? {}),
						inflationRate: effInflationStr,
						returnRate: migrateReturnRate(p, effInflationStr),
						incomeGrowthRate: migrateIncomeGrowthRate(p),
						returnOverrides:
							p.assumptions?.returnOverrides ??
							state.assumptions.returnOverrides ??
							[],
						portfolioMode:
							p.assumptions?.portfolioMode ??
							state.assumptions.portfolioMode ??
							'simple',
						incomeMode:
							p.assumptions?.incomeMode ??
							state.assumptions.incomeMode ??
							'afterTax',
						estimatedTaxRate:
							p.assumptions?.estimatedTaxRate ??
							state.assumptions.estimatedTaxRate ??
							'25',
					},
					_syncStatus: 'synced',
				});
			})
			.addCase(fetchFinancialProfile.rejected, (state) => {
				state._syncStatus = 'error';
			})
			.addCase(saveFinancialProfile.pending, (state) => {
				state._syncStatus = 'pending';
			})
			.addCase(saveFinancialProfile.fulfilled, (state, action) => {
				Object.assign(state, action.payload, { _syncStatus: 'synced' });
			})
			.addCase(saveFinancialProfile.rejected, (state) => {
				state._syncStatus = 'error';
			});
	},
});

export const {
	setAge,
	setSavings,
	setInvestedSavings,
	applyQuickStart,
	addIncomeEntry,
	updateIncomeEntry,
	removeIncomeEntry,
	addExpenseEntry,
	updateExpenseEntry,
	removeExpenseEntry,
	addLifestyleAsset,
	updateLifestyleAsset,
	removeLifestyleAsset,
	addSurplusAllocation,
	updateSurplusAllocation,
	removeSurplusAllocation,
	upsertCashRunway,
	setCurrentRunwaySaved,
	addHolding,
	updateHolding,
	confirmCostBasis,
	reviewHoldingAssumption,
	clearHoldingReview,
	removeHolding,
	addDraftHolding,
	activateHolding,
	changeHoldingTicker,
	discardEmptyDraftHoldings,
	addEquityGrant,
	updateEquityGrant,
	removeEquityGrant,
	updateAssumptions,
	setPortfolioMode,
	setReturnOverride,
	removeReturnOverride,
	clearReturnOverrides,
} = financialSlice.actions;

// Public max-year cap under its historical name.
export const MAX_RETURN_OVERRIDE_YEARS = MAX_OVERRIDE_YEARS;

// Re-exports keep every `from '.../store/financialSlice'` import valid after the
// split. Types, constants, and thunks now live in sibling modules.
export type {
	AssetClass,
	Assumptions,
	AvgPriceSource,
	EquityGrant,
	EquityType,
	ExpenseEntry,
	ExpenseFlexibility,
	ExpenseFrequency,
	ExpenseGroup,
	FinancialState,
	Frequency,
	IncomeCategory,
	IncomeEntry,
	IncomeFrequency,
	IncomeMode,
	LifestyleAsset,
	LifestyleAssetKind,
	LiquidityStatus,
	PortfolioMode,
	PriceHistoryPoint,
	ReturnOverride,
	ReviewedAssumptions,
	StockHolding,
	SurplusAllocationEntry,
	SurplusAllocationGroup,
} from './financialTypes';
export { CASH_RUNWAY_ALLOCATION_ID, isDraftHolding } from './financialTypes';
export { DEFAULT_ASSUMPTIONS } from './financialDefaults';
export { fetchFinancialProfile, saveFinancialProfile } from './financialThunks';

export default financialSlice.reducer;
